import { TimelineItem } from "@/models/legislation/Timeline";
import { ConsolidationPriorityMode } from "@/models/consolidation";
import { parseSignatureDate } from "@/lib/timelineDates";
import {
  DOCUMENT_TYPE_ID_BEKENDTGØRELSE,
  DOCUMENT_TYPE_ID_LOV,
  DOCUMENT_TYPE_ID_LOVBEKENDTGØRELSE,
} from "@/utils/legislationDocumentTypeUtils";

/**
 * Pure window/scope derivation for the prioritization timeline picker.
 *
 * A consolidation window opens at every anchor (LBK 30 / BEK 60 anywhere, or
 * the original Hovedlov 10 at index 0) and runs until the next anchor —
 * mirroring the boundary rule in the backend's window derivation
 * (services/sources-references/consolidation_queue/windows.py `is_boundary`
 * and ConsolidationTimelineHelper). Only anchors are valid prioritization
 * pivots (`consolidation_priorities.pivot_window_anchor_external_id`).
 */

/** True when the doc at `index` of the flat timeline opens a window. */
export const isWindowAnchor = (
  doc: Pick<TimelineItem, "documentTypeId">,
  index: number
): boolean =>
  doc.documentTypeId === DOCUMENT_TYPE_ID_LOVBEKENDTGØRELSE ||
  doc.documentTypeId === DOCUMENT_TYPE_ID_BEKENDTGØRELSE ||
  (doc.documentTypeId === DOCUMENT_TYPE_ID_LOV && index === 0);

export type TimelineDocWindow = {
  doc: TimelineItem;
  /** 0-based window ordinal the doc belongs to (its own window for anchors). */
  windowIndex: number;
  isAnchor: boolean;
};

/**
 * Assigns every doc of a flat timeline to its window. Docs before the first
 * anchor (defensive; should not occur) join window 0.
 */
export const assignWindows = (docs: TimelineItem[]): TimelineDocWindow[] => {
  let windowIndex = -1;
  return docs.map((doc, index) => {
    const isAnchor = isWindowAnchor(doc, index);
    if (isAnchor) windowIndex += 1;
    return { doc, windowIndex: Math.max(windowIndex, 0), isAnchor };
  });
};

/**
 * Anchor-year cutoff for consolidatable windows — mirrors DEFAULT_MIN_YEAR in
 * services/sources-references/consolidation_queue/enqueue.py (and the bulk
 * populator): windows anchored before this are never enqueued.
 */
export const MIN_ANCHOR_YEAR = 2016;

const anchorYear = (doc: TimelineItem): number | null => {
  if (typeof doc.year === "number" && doc.year > 0) return doc.year;
  const parsed = parseSignatureDate(doc.signatureDate);
  return parsed ? parsed.getFullYear() : null;
};

/**
 * Window indexes the consolidation solution actually consolidates — the
 * enqueue's policy (windows.py `_items_for_window`): the ANCHOR's year must be
 * >= MIN_ANCHOR_YEAR, and the window must hold at least one change law (a
 * fresh trailing boundary with nothing after it produces no queue rows).
 */
export const getValidWindowIndexes = (
  entries: TimelineDocWindow[]
): ReadonlySet<number> => {
  const anchors = new Map<number, TimelineDocWindow>();
  const windowsWithChanges = new Set<number>();
  for (const entry of entries) {
    if (entry.isAnchor) anchors.set(entry.windowIndex, entry);
    else windowsWithChanges.add(entry.windowIndex);
  }

  const valid = new Set<number>();
  for (const [windowIndex, anchor] of anchors) {
    const year = anchorYear(anchor.doc);
    if (
      year !== null &&
      year >= MIN_ANCHOR_YEAR &&
      windowsWithChanges.has(windowIndex)
    ) {
      valid.add(windowIndex);
    }
  }
  return valid;
};

/**
 * The set of accession numbers a prioritization covers, given the selected
 * pivot anchor and scope mode — mirrors the backend scope predicate
 * (urgent_poller.SCOPE_PREDICATE_SQL), evaluated positionally, restricted to
 * the windows the consolidation solution actually consolidates
 * (getValidWindowIndexes). FULL covers every valid window even before a pivot
 * is chosen; the other modes need the pivot.
 */
export const getCoveredAccessions = (
  entries: TimelineDocWindow[],
  pivotExternalId: string | null,
  mode: ConsolidationPriorityMode
): Set<string> => {
  const validWindows = getValidWindowIndexes(entries);
  const collect = (predicate: (e: TimelineDocWindow) => boolean): Set<string> =>
    new Set(
      entries
        .filter((e) => validWindows.has(e.windowIndex) && predicate(e))
        .map((e) => e.doc.accessionNumber)
        .filter((a): a is string => Boolean(a))
    );

  if (mode === "FULL") return collect(() => true);

  const pivot = entries.find(
    (e) => e.isAnchor && e.doc.accessionNumber === pivotExternalId
  );
  if (!pivot) return new Set();

  if (mode === "WINDOW")
    return collect((e) => e.windowIndex === pivot.windowIndex);
  if (mode === "FORWARD")
    return collect((e) => e.windowIndex >= pivot.windowIndex);
  return collect((e) => e.windowIndex <= pivot.windowIndex); // UP_TO
};

/** True when the mode also covers documents ingested in the future. */
export const coversFutureDocuments = (
  mode: ConsolidationPriorityMode
): boolean => mode === "FORWARD" || mode === "FULL";

/**
 * Builds the single root node CompactTimeline expects from normalized timeline
 * items: a BEK tree (single root with children) passes through, a flat law
 * timeline is converted to a single-branch tree — the same conversion the
 * legislation document view performs for its own timeline.
 */
export const buildTimelineRoot = (
  items: TimelineItem[] | undefined
): TimelineItem | null => {
  if (!items || items.length === 0) return null;
  if (items.length === 1) return items[0];

  let currentNode: TimelineItem | null = null;
  for (let i = items.length - 1; i >= 0; i--) {
    const item = { ...items[i] };
    if (currentNode) {
      item.children = [currentNode];
    }
    currentNode = item;
  }
  return currentNode;
};
